import type { FolderCount, KindCount } from './membership';
import { totalOf } from './membership';

/**
 * What a folder holds — two numbers, never summed.
 *
 * An absent kind renders nothing, not a zero: a folder with no arcs is not a
 * folder with zero arcs, and printing the zero puts a lane of them down the
 * tree for the 13 folders that hold only thoughts.
 *
 * `+N deeper` appears only where held and covered differ, which is what keeps
 * a folder whose whole content sits one level down from reading as empty.
 *
 * Everything here is `metaMono` at `inkFaint` — Meta's own default. Never
 * `ink` at an alpha: the head's ink is three solid tokens.
 */
export function Counts({
  count,
  deep,
  sayDeeper = true,
}: {
  count: FolderCount | undefined;
  deep?: boolean;
  /** False where the surface says it in words instead — the head spells out
   *  `N with everything deeper`, and printing both is one fact twice. */
  sayDeeper?: boolean;
}) {
  if (!count) return null;
  const shown: KindCount = deep ? count.covered : count.held;
  const extra = deep || !sayDeeper ? 0 : totalOf(count.covered) - totalOf(count.held);
  if (shown.arcs === 0 && shown.notes === 0 && extra === 0) return null;

  return (
    <span className="fd-counts">
      {shown.arcs > 0 && (
        <span className="fd-count" title={`${shown.arcs} arcs`}>
          <ArcGlyph />
          <span className="t-metaMono">{shown.arcs.toLocaleString()}</span>
        </span>
      )}
      {shown.notes > 0 && (
        <span className="fd-count" title={`${shown.notes} thoughts`}>
          <CaptureGlyph />
          <span className="t-metaMono">{shown.notes.toLocaleString()}</span>
        </span>
      )}
      {extra > 0 && <span className="t-metaMono">+{extra.toLocaleString()} deeper</span>}
    </span>
  );
}

/** The words, for the one place a count is the sentence rather than a lane.
 *  Singular is real: a folder holding one says `1 arc`. */
export function countsSentence(count: KindCount): string {
  const parts: string[] = [];
  if (count.arcs > 0) parts.push(`${count.arcs.toLocaleString()} ${count.arcs === 1 ? 'arc' : 'arcs'}`);
  if (count.notes > 0) parts.push(`${count.notes.toLocaleString()} ${count.notes === 1 ? 'thought' : 'thoughts'}`);
  return parts.join(' · ');
}

/** A bow — the shape the word names, and nothing else on the head wears it. */
function ArcGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" className="fd-count-glyph">
      <path
        d="M1.6 9.4a4.4 4.4 0 0 1 8.8 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Two stacked bars. Deliberately not the mic: a folder's thoughts are
 *  recordings AND written notes, and `journal` is 1,129 of the latter. */
function CaptureGlyph() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true" className="fd-count-glyph">
      <rect x="1.4" y="2.6" width="9.2" height="2.2" rx="1.1" fill="currentColor" />
      <rect x="1.4" y="6.6" width="6.4" height="2.2" rx="1.1" fill="currentColor" opacity="0.55" />
    </svg>
  );
}
